import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminImpersonation from "@/components/AdminImpersonation";
import {
  ArrowsClockwiseIcon, SignOutIcon, CaretDownIcon, PlusIcon,
  TrashIcon, CheckIcon, EyeIcon, XIcon,
} from "@phosphor-icons/react";
import { formatNumber } from "@/lib/format";

const TopNav = ({ channels = [], activeId, onSwitch, onAddChannel, onDisconnect, onSync, syncing, showSync }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = channels.find((c) => c.channel_id === activeId);

  const stopImpersonate = async () => {
    try {
      await api.post("/admin/stop-impersonate");
      toast.success("Back to your admin account");
      window.location.reload();
    } catch (e) {
      toast.error("Could not exit view-as");
    }
  };

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <header className="sticky top-0 z-30 bg-black/60 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <img src="/favicon.svg" alt="Content Overwatch" className="w-9 h-9 shrink-0" />
          <span className="font-rajdhani font-bold tracking-[0.15em] uppercase text-sm hidden md:block shrink-0">
            CONTENT<span className="text-[#16C784]"> OVERWATCH</span>
          </span>

          {/* Channel switcher */}
          {channels.length > 0 && (
            <div className="relative ml-1 md:ml-2 md:pl-3 md:border-l border-[#222226]" ref={ref}>
              <button
                data-testid="channel-switcher"
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-2 border border-[#16C784]/40 bg-[#16C784]/5 hover:bg-[#16C784]/15 px-2.5 py-1.5 rounded-sm transition-colors duration-200 max-w-[240px]"
              >
                {active?.thumbnail && (
                  <img src={active.thumbnail} alt="" referrerPolicy="no-referrer" className="w-6 h-6 rounded-full border border-[#222226]" />
                )}
                <span className="font-rajdhani font-semibold text-sm text-white truncate">{active?.title || "Select channel"}</span>
                <span className="hidden sm:inline font-mono text-[9px] uppercase tracking-wider text-[#16C784] shrink-0">Switch / Add</span>
                <CaretDownIcon size={14} className={`text-[#16C784] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
              </button>

              {open && (
                <div data-testid="channel-menu"
                  className="absolute left-0 top-full mt-2 w-72 bg-[#0C0C0E] border border-[#222226] rounded-sm shadow-2xl overflow-hidden"
                  style={{ boxShadow: "0 0 30px rgba(0,0,0,0.6)" }}>
                  <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#5a5a63] border-b border-[#1a1a1e]">
                    Your Channels · {channels.length}
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {channels.map((c) => (
                      <div
                        key={c.channel_id}
                        data-testid={`channel-option-${c.channel_id}`}
                        className={`group flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-white/5 transition-colors duration-150 ${c.channel_id === activeId ? "bg-white/[0.03]" : ""}`}
                        onClick={() => { onSwitch(c.channel_id); setOpen(false); }}
                      >
                        {c.thumbnail && <img src={c.thumbnail} alt="" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full border border-[#222226] shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="font-rajdhani font-semibold text-sm text-white truncate flex items-center gap-1.5">
                            {c.title}
                            {c.channel_id === activeId && <CheckIcon size={13} className="text-[#16C784] shrink-0" weight="bold" />}
                          </div>
                          <div className="font-mono text-[10px] text-[#5a5a63]">{formatNumber(c.subscriber_count)} subs · {formatNumber(c.video_count)} videos</div>
                        </div>
                        <button
                          data-testid={`remove-channel-${c.channel_id}`}
                          onClick={(e) => { e.stopPropagation(); if (window.confirm(`Remove ${c.title}?`)) onDisconnect(c.channel_id); }}
                          className="opacity-0 group-hover:opacity-100 text-[#5a5a63] hover:text-[#16C784] transition-all duration-150 p-1 shrink-0"
                          title="Remove channel"
                        >
                          <TrashIcon size={15} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    data-testid="add-channel-button"
                    onClick={() => { onAddChannel(); setOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-3 border-t border-[#1a1a1e] text-[#16C784] hover:bg-[#16C784]/10 font-rajdhani font-bold uppercase tracking-wider text-xs transition-colors duration-200"
                  >
                    <PlusIcon size={15} weight="bold" /> Add another channel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {user?.is_admin && <AdminImpersonation impersonating={user?.impersonating} />}
          {showSync && (
            <button
              data-testid="sync-button"
              onClick={onSync}
              disabled={syncing}
              className="flex items-center gap-2 border border-[#16C784] text-[#16C784] hover:bg-[#16C784]/10 disabled:opacity-50 font-rajdhani uppercase tracking-wider text-xs px-4 py-2 transition-colors duration-300"
            >
              <ArrowsClockwiseIcon size={15} className={syncing ? "spin-slow" : ""} />
              <span className="hidden sm:inline">{syncing ? "Syncing" : "Sync"}</span>
            </button>
          )}
          <div className="flex items-center gap-2">
            {user?.picture && (
              <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" className="w-8 h-8 rounded-full border border-[#222226]" />
            )}
            <button
              data-testid="logout-button"
              onClick={logout}
              className="text-[#A0A0AB] hover:text-[#16C784] transition-colors duration-200 p-1"
              title="Sign out"
            >
              <SignOutIcon size={20} />
            </button>
          </div>
        </div>
      </div>

      {user?.impersonating && (
        <div
          data-testid="impersonation-banner"
          className="bg-[#FFB800] text-black"
        >
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-1.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <EyeIcon size={15} weight="fill" className="shrink-0" />
              <span className="font-rajdhani font-bold uppercase tracking-wider text-xs truncate">
                Admin view-as — viewing {user.impersonating.name || user.impersonating.email}
                <span className="hidden sm:inline font-mono normal-case tracking-normal font-normal opacity-80"> ({user.impersonating.email})</span>
              </span>
            </div>
            <button
              data-testid="impersonation-exit"
              onClick={stopImpersonate}
              className="flex items-center gap-1 border border-black/40 hover:bg-black/10 font-rajdhani font-bold uppercase tracking-wider text-[11px] px-2.5 py-1 transition-colors shrink-0"
            >
              <XIcon size={12} weight="bold" /> Exit view
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default TopNav;
